import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import {
  createMuiTheme,
  ThemeProvider as MUIThemeProvider,
} from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import { ptBR } from "@material-ui/core/locale";

const ThemeContext = createContext();
const STORAGE_KEY = "darkMode";
const getInitialDarkMode = () => {
  const storedMode = localStorage.getItem(STORAGE_KEY);
  if (storedMode !== null) {
    return storedMode === "true";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const getThemeLocale = () => {
  const i18nLocale = localStorage.getItem("i18nextLng");

  if (!i18nLocale) {
    return undefined;
  }

  const browserLocale =
    i18nLocale.substring(0, 2) + i18nLocale.substring(3, 5);

  return browserLocale === "ptBR" ? ptBR : undefined;
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      const nextMode = !prevMode;
      localStorage.setItem(STORAGE_KEY, String(nextMode));
      return nextMode;
    });
  };

  const theme = useMemo(
    () =>
      createMuiTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: darkMode
                ? "inset 0 0 6px rgba(0, 0, 0, 0.45)"
                : "inset 0 0 6px rgba(0, 0, 0, 0.18)",
              backgroundColor: darkMode ? "#4b5563" : "#cbd5e1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: darkMode ? "#111827" : "#e5e7eb",
            },
          },
          palette: {
            type: darkMode ? "dark" : "light",
            primary: { main: "#2576d2" },
            secondary: { main: darkMode ? "#f97316" : "#d97706" },
            background: darkMode
              ? {
                  default: "#12161f",
                  paper: "#1b2330",
                }
              : {
                  default: "#f4f6fb",
                  paper: "#ffffff",
                },
            text: darkMode
              ? {
                  primary: "#e5e7eb",
                  secondary: "#94a3b8",
                }
              : {
                  primary: "#0f172a",
                  secondary: "#475569",
                },
          },
          custom: {
            appBar: darkMode ? "#18202c" : "#ffffff",
            panel: darkMode ? "#18202c" : "#eef2f7",
            panelSoft: darkMode ? "#243041" : "#f8fafc",
            border: darkMode
              ? "rgba(148, 163, 184, 0.18)"
              : "rgba(15, 23, 42, 0.12)",
            inboundMessage: darkMode ? "#243041" : "#ffffff",
            inboundQuoted: darkMode ? "#1f2937" : "#f0f0f0",
            outboundMessage: darkMode ? "#1d4f45" : "#dcf8c6",
            outboundQuoted: darkMode ? "#173f38" : "#cfe9ba",
            infoBadge: darkMode ? "#1f3449" : "#e1f3fb",
            actionIcon: darkMode ? "#94a3b8" : "#6b7280",
          },
          overrides: {
            MuiPaper: {
              outlined: {
                borderColor: darkMode
                  ? "rgba(148, 163, 184, 0.18)"
                  : "rgba(15, 23, 42, 0.12)",
              },
            },
          },
        },
        getThemeLocale()
      ),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
